import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { toArrayBuffer } from '@affine/core/utils/array-buffer';
import { DebugLogger } from '@affine/debug';
import type { Workspace as WorkspaceInterface } from '@blocksuite/affine/store';
import { Entity, LiveData, yjsGetPath } from '@toeverything/infra';
import type { Observable } from 'rxjs';
import { Doc as YDoc, transact } from 'yjs';

import { DocsService } from '../../doc/services/docs';
import { WorkspaceImpl } from '../impls/workspace';
import type { WorkspaceScope } from '../scopes/workspace';
import { WorkspaceEngineService } from '../services/engine';

const BLOB_SOURCE_REFRESH_DELAY = 250;
const logger = new DebugLogger('affine:workspace-blob-source');

export class Workspace extends Entity {
  constructor(
    public readonly scope: WorkspaceScope,
    public readonly featureFlagService: FeatureFlagService
  ) {
    super();
  }

  readonly id = this.scope.props.openOptions.metadata.id;

  readonly openOptions = this.scope.props.openOptions;

  readonly meta = this.scope.props.openOptions.metadata;

  readonly flavour = this.meta.flavour;

  readonly rootYDoc = new YDoc({ guid: this.openOptions.metadata.id });

  _docCollection: WorkspaceInterface | null = null;

  private readonly blobSourceUpdateCleanups = new WeakMap<YDoc, () => void>();

  private loadBlobSource(doc: YDoc) {
    this.blobSourceUpdateCleanups.get(doc)?.();
    const source = {
      type: 'currentDoc' as const,
      workspaceId: this.id,
      docId: doc.guid,
    };
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      if (refreshTimer) return;
      refreshTimer = setTimeout(() => {
        refreshTimer = undefined;
        this.engine.blob
          .registerSource(source)
          .catch(error => logger.error('Failed to refresh blob source', error));
      }, BLOB_SOURCE_REFRESH_DELAY);
    };
    doc.on('update', refresh);
    this.blobSourceUpdateCleanups.set(doc, () => {
      doc.off('update', refresh);
      if (refreshTimer) clearTimeout(refreshTimer);
      this.blobSourceUpdateCleanups.delete(doc);
    });
    this.engine.blob
      .registerSource(source)
      .catch(error => logger.error('Failed to register blob source', error));
  }

  private unloadBlobSource(doc: YDoc) {
    this.blobSourceUpdateCleanups.get(doc)?.();
    this.engine.blob
      .unregisterSource({
        type: 'currentDoc',
        workspaceId: this.id,
        docId: doc.guid,
      })
      .catch(error => logger.error('Failed to unregister blob source', error));
  }

  get docCollection() {
    if (!this._docCollection) {
      this._docCollection = new WorkspaceImpl({
        id: this.openOptions.metadata.id,
        rootDoc: this.rootYDoc,
        featureFlagService: this.featureFlagService,
        blobSource: {
          get: async key => {
            const record = await this.engine.blob.get(key);
            return record
              ? new Blob([toArrayBuffer(record.data)], { type: record.mime })
              : null;
          },
          delete: async () => {
            return;
          },
          list: async () => {
            return [];
          },
          set: async (id, blob) => {
            await this.engine.blob.set({
              key: id,
              data: new Uint8Array(await blob.arrayBuffer()),
              mime: blob.type,
            });
            return id;
          },
          blobState$: key => this.engine.blob.blobState$(key),
          upload: key => this.engine.blob.upload(key),
          name: 'blob',
          readonly: false,
        },
        onLoadDoc: doc => {
          this.loadBlobSource(doc);
          this.engine.doc.connectDoc(doc);
        },
        onUnloadDoc: doc => {
          this.unloadBlobSource(doc);
        },
        onLoadAwareness: awareness =>
          this.engine.awareness.connectAwareness(awareness),
        onCreateDoc: docId =>
          this.docs.createDoc({ id: docId, skipInit: true }).id,
      });
    }
    return this._docCollection;
  }

  get docs() {
    return this.scope.get(DocsService);
  }

  get canGracefulStop() {
    // TODO
    return true;
  }

  get engine() {
    return this.framework.get(WorkspaceEngineService).engine;
  }

  name$ = LiveData.from<string | undefined>(
    yjsGetPath(this.rootYDoc.getMap('meta'), 'name') as Observable<
      string | undefined
    >,
    undefined
  );

  avatar$ = LiveData.from(
    yjsGetPath(this.rootYDoc.getMap('meta'), 'avatar') as Observable<
      string | undefined
    >,
    undefined
  );

  setAvatar(avatar: string) {
    transact(
      this.rootYDoc,
      () => {
        this.rootYDoc.getMap('meta').set('avatar', avatar);
      },
      { force: true }
    );
  }

  setName(name: string) {
    transact(
      this.rootYDoc,
      () => {
        this.rootYDoc.getMap('meta').set('name', name);
      },
      { force: true }
    );
  }

  override dispose(): void {
    this.docCollection.dispose();
  }
}
